import { useRef, useState } from "react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { useLang } from "@/contexts/LanguageContext";
import api, { getApiError } from "@/lib/api";

const MAX_MB = 5;

export default function usePhotoUpload() {
  const { t } = useLang();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const prepareFile = async (f) => {
    // Compress phone photos (often 8-10MB) client-side via canvas before upload.
    if (f.size <= MAX_MB * 1024 * 1024) return f;
    try {
      const compressed = await imageCompression(f, {
        maxSizeMB: 4,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: "image/jpeg",
      });
      return compressed;
    } catch {
      return f;
    }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = 3 - photos.length;
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      for (const original of toUpload) {
        const f = await prepareFile(original);
        if (f.size > MAX_MB * 1024 * 1024) {
          toast.error(t.valuator.photo_too_large);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f, original.name);
        const res = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, { path: res.data.path, preview: URL.createObjectURL(f) }]);
      }
    } catch (err) {
      toast.error(getApiError(err, t.valuator.photo_upload_failed));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  return { photos, uploading, fileRef, cameraRef, onPickFiles, removePhoto };
}
